/**
 * Frontend-specific stepped HP utilities.
 *
 * Shared types and Array↔Record conversions live in ./stepConversions.ts
 * (mirrored in graphql/src/util/viewer/hangingProtocol/stepConversions.ts).
 * This file adds frontend-only concerns:
 *   - Mutation helpers (stepToGqlInput, applyMirrorGroupsToStepViewports)
 *   - Viewport filtering (isViewportInLayoutBounds, isEmptyViewport)
 *   - Helper functions (hasSubSteps)
 */

import type {
  StepViewportEntryInput,
  StepLayoutEntryInput,
  StepSubStepInput,
  StepConfigurationInput,
} from 'generated/graphql';
import { ViewType, AllowedViewportType } from 'generated/graphql';
import type { ViewportTypeKeys } from 'config/constants';
import {
  applyMirrorGroups,
  type MutableViewportConfig,
} from 'domains/viewer/Viewer/StudiesBar/HangingProtocols/utils/viewportsConfigToHangingProtocol';
import { stripTypename } from 'utils/stripTypename';
import type {
  StepConfiguration,
  ViewportConfigurationEntry,
  GqlStepConfiguration,
} from './stepConversions';
import { parseLayoutString, formatLayoutString } from './stepConversions';
import { calculateSubStepTotal } from './hpStepController';
import { viewportIdToConfig } from 'domains/viewer/Viewer/viewerUtils';

// Re-export shared types and conversions so existing imports don't break
export { viewportsArrayToRecord, layoutsArrayToRecord, stepFromGqlFormat } from './stepConversions';
export { parseLayoutString, formatLayoutString };
export type {
  StepConfigDetails,
  ViewportConfigurationEntry,
  SubStep,
  StepConfiguration,
  GqlStepConfiguration,
  StepViewportEntry,
  StepLayoutEntry,
  StepSubStep,
} from './stepConversions';

// ── view_type bridges ────────────────────────────────────────────────────
// A step viewport's persisted `view_type` is `AllowedViewportType`, a subset of
// the broader frontend `ViewType`/`ViewportTypeKeys` that omits `THREE_D_MIP`
// (a PET/CT-only rendering mode that is not stored as a distinct step view
// type). The enums share identical string values, so conversion is identity
// apart from the MIP fallback below.

const ALLOWED_VIEWPORT_TYPE_VALUES = new Set<string>(Object.values(AllowedViewportType));

/** True when `value` is a valid `AllowedViewportType` enum value. */
export function isAllowedViewportType(value: unknown): value is AllowedViewportType {
  return typeof value === 'string' && ALLOWED_VIEWPORT_TYPE_VALUES.has(value);
}

/**
 * Converts a frontend view type into the persistable `AllowedViewportType`.
 * Returns null for unknown/unsupported values so we never persist a string that
 * isn't a valid GraphQL enum member.
 */
export function toAllowedViewportType(
  viewType: Nullable<ViewType | ViewportTypeKeys | string>
): AllowedViewportType | null {
  if (viewType == null) return null;
  // THREE_D_MIP cannot be persisted on a step; fall back to THREE_D_DRE.
  if (viewType === ViewType.ThreeDMip) return AllowedViewportType.ThreeDDre;
  return isAllowedViewportType(viewType) ? viewType : null;
}

/** Reads a persisted `view_type` back into a frontend `ViewType` (default 2D). */
export function fromAllowedViewportType(viewType: Nullable<AllowedViewportType>): ViewType {
  return (viewType as unknown as ViewType) ?? ViewType.TwoDDre;
}

/**
 * Returns true if the given viewport ID falls within the bounds of the provided layouts.
 * Viewport IDs follow the format "Viewport-{windowId}-{row}-{col}".
 */
export function isViewportInLayoutBounds(
  viewportId: string,
  layouts: Record<string, string>
): boolean {
  const { windowId, rowIndex, colIndex } = viewportIdToConfig(viewportId);

  const layoutStr = layouts[windowId];
  if (layoutStr == null) return false;

  const [rows, cols] = parseLayoutString(layoutStr);
  return rowIndex < rows && colIndex < cols;
}

/**
 * Returns true if the viewport entry is effectively empty (unassigned slot).
 * A viewport is empty when it has no display name AND no series criteria.
 */
export function isEmptyViewport(entry: ViewportConfigurationEntry): boolean {
  const hasDisplayName = entry.display_name != null && entry.display_name !== '';
  const hasCriteria = Boolean(entry.criteria?.length);
  const hasSeriesCriteriaText =
    entry.series_criteria_source_text != null && entry.series_criteria_source_text !== '';
  return !hasDisplayName && !hasCriteria && !hasSeriesCriteriaText;
}

/**
 * Convert internal Record<viewportId, VCE> to GQL StepViewportEntryInput[].
 * Strips is_clear entries, effectively-empty viewports, and viewports whose
 * coordinates fall outside the layout bounds.
 */
export function viewportsRecordToInputArray(
  viewports: Record<string, ViewportConfigurationEntry>,
  layouts: Record<string, string>
): StepViewportEntryInput[] {
  return Object.entries(viewports)
    .filter(([viewportId, entry]) => {
      if (entry.is_clear) return false;
      if (isEmptyViewport(entry)) return false;
      if (!isViewportInLayoutBounds(viewportId, layouts)) return false;
      return true;
    })
    .map(([viewportId, entry]) => ({
      viewport_id: viewportId,
      display_name: entry.display_name ?? viewportId,
      study_smid: entry.study_smid,
      series_smid: entry.series_smid,
      view_type: entry.view_type ?? null,
      series_criteria_source_text: entry.series_criteria_source_text ?? null,
      comparison_index: entry.comparison_index ?? null,
      criteria: entry.criteria ?? null,
      stack_smid: entry.stack_smid ?? null,
      viewport_mirror_group_id: entry.viewport_mirror_group_id ?? null,
    }));
}

/** Convert internal Record<windowId, layoutString> to GQL StepLayoutEntryInput[]. */
export function layoutsRecordToInputArray(layouts: Record<string, string>): StepLayoutEntryInput[] {
  return Object.entries(layouts).map(([windowId, layout]) => ({
    window_id: windowId,
    layout,
  }));
}

/**
 * Creates a stable grouping key from a viewport's series criteria.
 * Viewports with the same criteria key within a step should be assigned
 * the same series by the backend (mirror groups).
 */
function createCriteriaGroupKey(
  criteria: Nullable<ReadonlyArray<{ category: string; value: string }>>
): string | undefined {
  if (!criteria?.length) return undefined;
  return criteria
    .map((c) => `${c.category}:${c.value}`)
    .sort()
    .join('|');
}

/**
 * Applies mirror group IDs to stepped HP viewport entries using the same
 * algorithm as flat HPs ({@link applyMirrorGroups}). Viewports with identical
 * series criteria within a step are assigned the same mirror group ID, which
 * tells the backend to assign the same series to all of them.
 */
export function applyMirrorGroupsToStepViewports(
  viewports: StepViewportEntryInput[]
): StepViewportEntryInput[] {
  const configs: MutableViewportConfig[] = viewports.map((vp) => ({
    id: vp.viewport_id,
    viewType: (vp.view_type ?? 'TWO_D_DRE') as ViewType,
    comparisonIndex: vp.comparison_index ?? null,
    seriesId: createCriteriaGroupKey(vp.criteria),
    seriesDescriptions: [],
  }));

  const withMirrors = applyMirrorGroups(configs);

  return viewports.map((vp, i) => ({
    ...vp,
    viewport_mirror_group_id: withMirrors[i].viewportMirrorGroupId ?? null,
  }));
}

/** Convert an internal StepConfiguration to GQL StepConfigurationInput for mutations. */
export function stepToGqlInput(step: StepConfiguration): StepConfigurationInput {
  return {
    smid: step.smid,
    display_name: step.display_name,
    layouts: layoutsRecordToInputArray(step.layouts),
    config_details: step.config_details,
    viewports: applyMirrorGroupsToStepViewports(
      viewportsRecordToInputArray(step.viewports, step.layouts)
    ),
    sub_steps: step.sub_steps
      ? step.sub_steps.map((ss) => ({
          viewports: applyMirrorGroupsToStepViewports(
            viewportsRecordToInputArray(ss, step.layouts)
          ),
        }))
      : null,
  };
}

/**
 * Projects a step in GQL *output* shape (`StepConfiguration`, as returned by
 * matchHangingProtocolByWorklistsAndStudies) onto `StepConfigurationInput` for use in a mutation.
 *
 * Needed because the two types are not interchangeable: `StepConfiguration` also carries `order`,
 * which `StepConfigurationInput` does not define, so passing a fetched step straight back into a
 * mutation makes GraphQL reject the entire variable with BAD_USER_INPUT (VX-4713). This is an
 * allowlist of the input type's own fields rather than a "delete the fields we know are bad" strip,
 * so a future output-only field can't reintroduce the same failure. Every nested type (layouts,
 * config_details, viewports, sub_steps) has identical field names on the input side, so those only
 * need `__typename` removed.
 *
 * Distinct from {@link stepToGqlInput}, which converts the internal Record-based
 * `StepConfiguration` used for frontend step state.
 */
export function gqlStepToInput(step: GqlStepConfiguration): StepConfigurationInput {
  return {
    smid: step.smid,
    display_name: step.display_name,
    layouts: stripTypename(step.layouts) as StepLayoutEntryInput[],
    config_details: stripTypename(step.config_details),
    viewports: stripTypename(step.viewports) as StepViewportEntryInput[],
    sub_steps: stripTypename(step.sub_steps) as Nullable<StepSubStepInput[]>,
  };
}

// ============================================================================
// Helper functions
// ============================================================================

export type {
  StepViewportEntryInput,
  StepLayoutEntryInput,
  StepSubStepInput,
  StepConfigurationInput,
};

/**
 * Helper to check if a step has sub-steps for prior scrolling.
 */
export function hasSubSteps(step: Nullable<StepConfiguration>): boolean {
  return calculateSubStepTotal(step) > 0;
}
